'use client';

import { c as _c } from "react/compiler-runtime";
import { DROPZONE_ACCEPT } from '@shared/all/uploadTypes';
import toast from '@shared/utils/helpers/toast';
import { Camera as CameraIcon } from 'lucide-react';
import { useState } from 'react';
import { type FileRejection, useDropzone } from 'react-dropzone';

// Mirrors RAW_MAX_SIZE in apps/server/src/routers/uploads.ts.
const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;
import Modal, { ModalContent, ModalHeader } from '../HOC/Modal';
import { Camera } from './Camera';
import CropImage from './CropImage';
type Props = {
  closeHandler: () => void;
  title?: string;
  callbackFn: (item: File) => void;
  multiple?: boolean;
  crop?: {
    enabled: boolean;
    aspect?: number;
    circularCrop?: boolean;
  };
  enableCamera?: boolean;
};
const UploadModal = t0 => {
  const $ = _c(47);
  const {
    closeHandler,
    title: t1,
    callbackFn,
    multiple,
    crop,
    enableCamera: t2
  } = t0;
  const title = t1 === undefined ? "Upload Media" : t1;
  const enableCamera = t2 === undefined ? false : t2;
  const [fileSelected, setFileSelected] = useState(false);
  const [sourceImageLocalUrl, setSourceImageLocalUrl] = useState("");
  const [cropImageModalOpen, setCropImageModalOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  let t3;
  if ($[0] !== callbackFn || $[1] !== closeHandler || $[2] !== crop?.enabled || $[3] !== multiple) {
    t3 = async (acceptedFiles, rejectedFiles) => {
      rejectedFiles.forEach(_temp);
      if (!multiple) {
        const file = acceptedFiles[0];
        if (file.size > MAX_UPLOAD_BYTES) {
          toast(`${file.name} exceeds the 100MB limit.`, "", "error");
          return;
        }
        const url = URL.createObjectURL(file);
        setSourceImageLocalUrl(url);
        if (crop?.enabled) {
          const reader = new FileReader();
          reader.onload = () => {
            setSourceImageLocalUrl(URL.createObjectURL(file));
            setCropImageModalOpen(true);
          };
          reader.readAsDataURL(file);
        } else {
          setFileSelected(true);
          await new Promise(_temp2);
          return callbackFn(file);
        }
      }
      if (multiple) {
        const accepted = acceptedFiles.filter(_temp3);
        if (accepted.length === 0) {
          return;
        }
        accepted.forEach((file_1, index) => {
          callbackFn(file_1);
          if (index === accepted.length - 1) {
            setFileSelected(true);
            setTimeout(() => closeHandler(), 500);
          }
        });
      }
    };
    $[0] = callbackFn;
    $[1] = closeHandler;
    $[2] = crop?.enabled;
    $[3] = multiple;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const onDropHandler = t3;
  let t4;
  if ($[5] !== onDropHandler) {
    t4 = photo => {
      if (photo.file) {
        onDropHandler([photo.file], []);
      }
    };
    $[5] = onDropHandler;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleCameraPhoto = t4;
  let t5;
  if ($[7] !== multiple || $[8] !== onDropHandler) {
    t5 = {
      onDrop: onDropHandler,
      multiple,
      accept: DROPZONE_ACCEPT
    };
    $[7] = multiple;
    $[8] = onDropHandler;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject
  } = useDropzone(t5);
  let t6;
  if ($[10] !== callbackFn) {
    t6 = image => {
      const file_2 = new File([image], "croppedImage.png", {
        type: "image/png"
      });
      callbackFn(file_2);
    };
    $[10] = callbackFn;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const afterImageCropHandler = t6;
  if (cameraOpen) {
    let t7;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = () => setCameraOpen(false);
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    let t8;
    if ($[13] !== handleCameraPhoto) {
      t8 = <Camera closeHandler={t7} setNewPhotoFcn={handleCameraPhoto} />;
      $[13] = handleCameraPhoto;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    return t8;
  }
  let t7;
  if ($[15] !== title) {
    t7 = <h2>{title}</h2>;
    $[15] = title;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== closeHandler || $[18] !== t7) {
    t8 = <ModalHeader closeHandler={closeHandler}>{t7}</ModalHeader>;
    $[17] = closeHandler;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== enableCamera || $[21] !== fileSelected || $[22] !== getInputProps || $[23] !== getRootProps || $[24] !== isDragActive || $[25] !== isDragReject || $[26] !== multiple) {
    t9 = !fileSelected && <><div className={`flex size-full flex-col items-center justify-center rounded-md border border-dashed border-ds-border-standard p-6 ${isDragReject ? "cursor-not-allowed" : "cursor-pointer"}`} {...getRootProps()} style={{
        cursor: isDragReject ? "not-allowed" : "pointer"
      }}><input {...getInputProps()} />{!isDragActive && <h2>Drop your file{multiple ? "(s)" : ""} here or{" "}<span>browse</span></h2>}{isDragActive && !isDragReject && <h2>Drop it like it's hot!</h2>}{isDragReject && <h2>Nope, can't upload this.</h2>}<p style={{
          textAlign: "center"
        }}>Max. File Size: 100MB<br />Images, GIFs, videos, PDFs, Word and Excel.</p></div>{enableCamera && <div className="mt-4 flex flex-col items-center"><div className="flex w-full items-center gap-3"><div className="h-px flex-1 bg-ds-border-standard" /><span className="text-sm text-textSecondary">or</span><div className="h-px flex-1 bg-ds-border-standard" /></div><button type="button" className="mt-3 flex items-center gap-2 rounded-md border border-ds-border-standard px-4 py-2 text-sm font-emphasis text-textPrimary transition-colors hover:bg-background-light" onClick={() => setCameraOpen(true)}><CameraIcon size={18} />Take Photo</button></div>}</>;
    $[20] = enableCamera;
    $[21] = fileSelected;
    $[22] = getInputProps;
    $[23] = getRootProps;
    $[24] = isDragActive;
    $[25] = isDragReject;
    $[26] = multiple;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== fileSelected || $[29] !== multiple) {
    t10 = fileSelected && <div className="flex w-full flex-col items-center justify-center p-6"><div className="mb-4 text-6xl text-green-500">✓</div><h2 className="text-lg font-semibold text-green-600">File{multiple ? "s" : ""} Selected!</h2><p className="text-textSecondary">Your file{multiple ? "s" : ""} will be uploaded when you submit.</p></div>;
    $[28] = fileSelected;
    $[29] = multiple;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10 || $[32] !== t9) {
    t11 = <ModalContent>{t9}{t10}</ModalContent>;
    $[31] = t10;
    $[32] = t9;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== closeHandler || $[35] !== t11 || $[36] !== t8) {
    t12 = <Modal closeHandler={closeHandler}>{t8}{t11}</Modal>;
    $[34] = closeHandler;
    $[35] = t11;
    $[36] = t8;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== afterImageCropHandler || $[39] !== crop?.aspect || $[40] !== crop?.circularCrop || $[41] !== cropImageModalOpen || $[42] !== sourceImageLocalUrl) {
    t13 = cropImageModalOpen && <CropImage aspect={crop?.aspect} src={sourceImageLocalUrl} afterImageCropHandler={afterImageCropHandler} circularCrop={crop?.circularCrop} closeHandler={() => setCropImageModalOpen(false)} />;
    $[38] = afterImageCropHandler;
    $[39] = crop?.aspect;
    $[40] = crop?.circularCrop;
    $[41] = cropImageModalOpen;
    $[42] = sourceImageLocalUrl;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== t12 || $[45] !== t13) {
    t14 = <>{t12}{t13}</>;
    $[44] = t12;
    $[45] = t13;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  return t14;
};
export default UploadModal;
function _temp(rejection) {
  toast(`${rejection.file.name} is not an accepted file type.`, "", "error");
}
function _temp2(resolve) {
  return setTimeout(resolve, 500);
}
function _temp3(file_0) {
  if (file_0.size > MAX_UPLOAD_BYTES) {
    toast(`${file_0.name} exceeds the 100MB limit.`, "", "error");
    return false;
  }
  return true;
}